/**
 * Surface context: locale + translator, BFF transport, current roles, clock and preloaded data.
 * Screens read everything through useSurface() so they render identically in the browser and in tests.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { createTranslator, dirFor, isLocale, translatorFor, type Catalog, type Locale, type Translate } from '@qm/i18n';
import { useTextOverrides } from '../../../lib/ui-text-hook';
import { createHttp, type Http } from './http';

export interface SurfaceValue {
  locale: Locale;
  dir: 'ltr' | 'rtl';
  t: Translate;
  http: Http;
  roles: readonly string[];
  now: () => Date;
  /** keyed initial data (tests / SSR); a key present here is rendered without fetching */
  preload: Readonly<Record<string, unknown>>;
}

/** Local catalog first, then the shared module catalogs, then English. */
export function makeTranslator(local: { en: Catalog; ar: Catalog }, locale: Locale): Translate {
  const own = translatorFor(local, locale);
  const shared = createTranslator(locale);
  return (key, params) => (key in local.en ? own(key, params) : shared(key, params));
}

export function detectLocale(): Locale {
  try {
    const fromStore = globalThis.localStorage?.getItem('qm.locale');
    if (isLocale(fromStore)) return fromStore;
  } catch { /* storage may be blocked */ }
  const lang = globalThis.document?.documentElement.lang?.slice(0, 2);
  return isLocale(lang) ? lang : 'en';
}

const Ctx = createContext<SurfaceValue | null>(null);

export interface SurfaceProviderProps {
  catalog: { en: Catalog; ar: Catalog };
  children: ReactNode;
  locale?: Locale;
  http?: Http;
  roles?: readonly string[];
  now?: () => Date;
  preload?: Record<string, unknown>;
}

export function SurfaceProvider({ catalog, children, locale, http, roles, now, preload }: SurfaceProviderProps) {
  const parent = useContext(Ctx);
  const l = locale ?? parent?.locale ?? detectLocale();
  const textVersion = useTextOverrides(l); // published staff wording (Portal text CMS): rebuilds the translator when it arrives
  const value = useMemo<SurfaceValue>(() => {
    return {
      locale: l,
      dir: dirFor(l),
      t: makeTranslator(catalog, l),
      http: http ?? parent?.http ?? createHttp(),
      roles: roles ?? parent?.roles ?? [],
      now: now ?? parent?.now ?? (() => new Date()),
      preload: { ...(parent?.preload ?? {}), ...(preload ?? {}) },
    };
  }, [catalog, l, textVersion, http, roles, now, preload, parent]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSurface(): SurfaceValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSurface outside SurfaceProvider');
  return v;
}
